/**
 * IndexedDB-backed store for history-entry photos.
 *
 * History entry metadata (sizing result, picked sizes, product info) lives in
 * localStorage — small, JSON-serializable. The user's *photo* is binary and
 * would blow the 5–10 MB localStorage quota at 50 entries; IndexedDB has no
 * such practical limit.
 *
 * Each photo is stored as a `Blob` keyed by the history entry's `id`. When
 * a history entry is deleted, call `deletePhoto(id)` to free the row.
 */

const DB_NAME = "primestyle-photos";
const STORE = "photos";
const VERSION = 1;

let _dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
	if (_dbPromise) return _dbPromise;
	_dbPromise = new Promise((resolve) => {
		try {
			if (typeof indexedDB === "undefined") return resolve(null);
			const req = indexedDB.open(DB_NAME, VERSION);
			req.onupgradeneeded = () => {
				const db = req.result;
				if (!db.objectStoreNames.contains(STORE)) {
					db.createObjectStore(STORE);
				}
			};
			req.onsuccess = () => resolve(req.result);
			req.onerror = () => resolve(null);
			req.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
	return _dbPromise;
}

// Prefix for try-on result blobs so they share the photos store without
// colliding with user-photo keys (`<id>` vs `result-<id>`).
const RESULT_PREFIX = "result-";

/** Save a photo Blob keyed by historyEntryId. Replaces if exists. */
export async function savePhoto(id: string, blob: Blob): Promise<boolean> {
	const db = await openDb();
	if (!db) return false;
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, "readwrite");
			tx.objectStore(STORE).put(blob, id);
			tx.oncomplete = () => resolve(true);
			tx.onerror = () => resolve(false);
			tx.onabort = () => resolve(false);
		} catch {
			resolve(false);
		}
	});
}

/** Load a photo Blob by historyEntryId, or null if not stored. */
export async function getPhoto(id: string): Promise<Blob | null> {
	const db = await openDb();
	if (!db) return null;
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, "readonly");
			const req = tx.objectStore(STORE).get(id);
			req.onsuccess = () => resolve(req.result instanceof Blob ? req.result : null);
			req.onerror = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
}

/** Delete a photo by historyEntryId. */
export async function deletePhoto(id: string): Promise<boolean> {
	const db = await openDb();
	if (!db) return false;
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, "readwrite");
			tx.objectStore(STORE).delete(id);
			tx.oncomplete = () => resolve(true);
			tx.onerror = () => resolve(false);
		} catch {
			resolve(false);
		}
	});
}

/** Save a try-on result Blob keyed by historyEntryId. Replaces if exists. */
export async function saveResult(id: string, blob: Blob): Promise<boolean> {
	return savePhoto(RESULT_PREFIX + id, blob);
}

/** Load a try-on result Blob by historyEntryId, or null if not stored. */
export async function getResult(id: string): Promise<Blob | null> {
	return getPhoto(RESULT_PREFIX + id);
}

/** Garbage-collect: keep only photos (and results) whose id is in `keepIds`.
 *  Uses a value cursor (openCursor) so records can be deleted during
 *  iteration — openKeyCursor's cursor.delete() throws InvalidStateError. */
export async function pruneToIds(keepIds: Set<string>): Promise<void> {
	const db = await openDb();
	if (!db) return;
	const expected = new Set<string>();
	for (const id of keepIds) {
		expected.add(id);
		expected.add(RESULT_PREFIX + id);
	}
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, "readwrite");
			const store = tx.objectStore(STORE);
			const cursorReq = store.openCursor();
			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result;
				if (!cursor) return;
				const key = String(cursor.key);
				if (!expected.has(key)) cursor.delete();
				cursor.continue();
			};
			tx.oncomplete = () => resolve();
			tx.onerror = () => resolve();
		} catch {
			resolve();
		}
	});
}
